import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, Filter, BarChart3, PieChart as PieChartIcon } from 'lucide-react';
import { IndustryBenchmark, BenchmarkTopic } from '@/types/materiality';
import { materialityService } from '@/utils/materialityService';

interface IndustryBenchmarksProps {}

export const IndustryBenchmarks: React.FC<IndustryBenchmarksProps> = () => {
  const [benchmarks, setBenchmarks] = useState<IndustryBenchmark[]>([]);
  const [selectedIndustry, setSelectedIndustry] = useState<string>('all');
  const [selectedSector, setSelectedSector] = useState<string>('all');
  const [selectedRegion, setSelectedRegion] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [chartView, setChartView] = useState<'bar' | 'pie'>('bar');

  // Fetch data from database
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log('Fetching industry benchmarks...');
        const benchmarksData = await materialityService.getIndustryBenchmarks();
        console.log('Received industry benchmarks:', benchmarksData);
        setBenchmarks(benchmarksData);
      } catch (error) {
        console.error('Error fetching industry benchmarks:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredBenchmarks = benchmarks.filter(benchmark => {
    if (selectedIndustry !== 'all' && benchmark.industry !== selectedIndustry) return false;
    if (selectedSector !== 'all' && benchmark.sector !== selectedSector) return false;
    if (selectedRegion !== 'all' && benchmark.region !== selectedRegion) return false;
    return true;
  });

  const industries = [...new Set(benchmarks.map(b => b.industry))];
  const sectors = [...new Set(benchmarks.map(b => b.sector))];
  const regions = [...new Set(benchmarks.map(b => b.region))];

  const chartData = filteredBenchmarks.map(benchmark => ({
    name: benchmark.industry,
    businessImpact: benchmark.averageBusinessImpact,
    stakeholderImportance: benchmark.averageStakeholderImportance,
    totalTopics: benchmark.totalTopics,
    highPriorityTopics: benchmark.highPriorityTopics
  }));

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  if (loading) {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Industry Benchmarks
            </CardTitle>
            <CardDescription>
              Compare your materiality assessment against industry peers
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-96 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-2"></div>
                <p className="text-sm text-gray-600">Loading benchmark data...</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Industry Benchmarks
              </CardTitle>
              <CardDescription>
                Compare your materiality assessment against industry peers
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant={chartView === 'bar' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setChartView('bar')}
                className="flex items-center gap-2"
              >
                <BarChart3 className="h-4 w-4" />
                Bar Chart
              </Button>
              <Button
                variant={chartView === 'pie' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setChartView('pie')}
                className="flex items-center gap-2"
              >
                <PieChartIcon className="h-4 w-4" />
                Pie Chart
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 mb-6">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              <span className="text-sm font-medium">Filter:</span>
            </div>
            <Select value={selectedIndustry} onValueChange={setSelectedIndustry}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Industry" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Industries</SelectItem>
                {industries.map(industry => (
                  <SelectItem key={industry} value={industry}>{industry}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedSector} onValueChange={setSelectedSector}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Sector" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sectors</SelectItem>
                {sectors.map(sector => (
                  <SelectItem key={sector} value={sector}>{sector}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedRegion} onValueChange={setSelectedRegion}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Region" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Regions</SelectItem>
                {regions.map(region => (
                  <SelectItem key={region} value={region}>{region}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="h-96 mb-6">
            {chartView === 'bar' ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="businessImpact" fill="#8884d8" name="Business Impact" />
                  <Bar dataKey="stakeholderImportance" fill="#82ca9d" name="Stakeholder Importance" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="highPriorityTopics"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Industry</TableHead>
                  <TableHead>Sector</TableHead>
                  <TableHead>Region</TableHead>
                  <TableHead>Year</TableHead>
                  <TableHead>Avg Business Impact</TableHead>
                  <TableHead>Avg Stakeholder Importance</TableHead>
                  <TableHead>Total Topics</TableHead>
                  <TableHead>High Priority Topics</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBenchmarks.map((benchmark) => (
                  <TableRow key={benchmark.id}>
                    <TableCell className="font-medium">{benchmark.industry}</TableCell>
                    <TableCell>{benchmark.sector}</TableCell>
                    <TableCell>{benchmark.region}</TableCell>
                    <TableCell>{benchmark.year}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{benchmark.averageBusinessImpact.toFixed(1)}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{benchmark.averageStakeholderImportance.toFixed(1)}</Badge>
                    </TableCell>
                    <TableCell>{benchmark.totalTopics}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{benchmark.highPriorityTopics}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {filteredBenchmarks.length === 0 && (
            <div className="text-center py-8">
              <TrendingUp className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No benchmark data found matching the current filters.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top Material Topics by Industry */}
      {filteredBenchmarks.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Top Material Topics by Industry</CardTitle>
            <CardDescription>
              Most frequently identified material topics across industries
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {filteredBenchmarks.map((benchmark) => (
                <div key={benchmark.id} className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-3">{benchmark.industry} - {benchmark.sector}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {benchmark.topics.slice(0, 6).map((topic, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm font-medium">{topic.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {topic.frequency}%
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}; 